import { useEffect, useMemo, useRef } from 'preact/compat';
import { HistoryManager } from './HistoryManager';

type Props<T> = {
  /**
   * The state that needs to be pushed to the history.
   */
  state: T;

  /**
   * Compares two states
   * @param a State A
   * @param b State B
   * @returns equality of the two states
   */
  comparator: (a?: T, b?: T) => boolean;

  /**
   * Whether history management is disabled. When it is disabled
   * the states will not be pushed to the history. Additionally, when it
   * changes from enabled to disabled, the history will be cleared.
   */
  isDisabled: boolean;

  /**
   * Maximum states that need to be stored in the history. This can only be set
   * during the initialization. Changing the value after the initialization will
   * not have any effect.
   */
  maxHistory?: number;
};

export type HistoryController<T> = Pick<
  HistoryManager<T>,
  'hasRedo' | 'hasUndo' | 'redo' | 'undo'
> & {
  /**
   * A method that can be called to toggle the history manager to
   * save or ignore states.
   * This is different from `isDisabled`; ignoring will not clear the history
   * while disabling clears the history.
   * @param doIgnore flag to indicate if this should ignore state updates
   */
  ignore: (doIgnore?: boolean) => void;
};

/**
 * A custom hook that provides history management. Using this hook, one can push
 * consecutive states to the stack. Then they can go to any point in the history
 * by performing undo/redo operations.
 * @param param0 The hook's props
 * @returns The history manager controller that can be used to manage the history.
 *
 * @template State A Generic representing the shape of the state.
 *
 * @example
 * function MyComponent(props) {
 *   const historyManager = useHistoryManager<MyValue>({
 *     state: props.value,
 *     comparator: (value1, value2) => value1 === value2
 *   });
 *
 *   return (
 *     <SomeComponent
 *       value={props.value}
 *       onValueChange={props.onValueChange}
 *       onUndo={() => {
 *         const undoValue = historyManager.undo();
 *         props.onValueChange(historyManager.undo());
 *       }}
 *       onRedo={() => {
 *         const redoValue = historyManager.redo();
 *         props.onValueChange(redoValue);
 *       }}  />
 *   );
 * }
 */
export function useHistoryManager<State>({
  state,
  comparator,
  isDisabled,
  maxHistory
}: Props<State>) {
  // We do not want to recreate history manager and only have a single version.
  const initialMaxHistoryRef = useRef(maxHistory);
  const historyManager = useMemo(
    () => new HistoryManager<State>({ maxHistory: initialMaxHistoryRef.current }),
    []
  );

  // We use a ref for determining if we should ignore state updates. This is needed
  // as we may not want to update the history when we are undoing/redoing.
  const isIgnoredRef = useRef(false);

  // We want to add the state to the history if it changes. But, we should not cause
  // a rerender as pushing a state to the history does not cause any UI changes. So we
  // will use refs instead of states.
  const previousStateRef = useRef<State>();
  // we only push the new state if
  // 1. it is different from the old state
  // 2. the history management is not disabled
  // 3. the states are not ignored
  if (!comparator(previousStateRef.current, state) && !isDisabled && !isIgnoredRef.current) {
    previousStateRef.current = state;
    historyManager.push(state);
  }

  // when the history management becomes disabled, we clear the history created so far
  if (isDisabled) {
    historyManager.clear();
  }

  // clear the history when the component is unmounted
  // we only want to clear history on unmount.
  // TODO: Disable eslint exhaustive-deps rule when it is enabled
  // doing it now results in eslint error, saying the rule is not found.
  useEffect(() => () => historyManager.clear(), [historyManager]);

  return useMemo<HistoryController<State>>(
    () => ({
      get hasRedo() {
        return historyManager.hasRedo;
      },
      get hasUndo() {
        return historyManager.hasUndo;
      },
      redo: () => historyManager.redo(),
      undo: () => historyManager.undo(),
      ignore(doIgnore = true) {
        isIgnoredRef.current = !!doIgnore;
      }
    }),
    [historyManager]
  );
}
